import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { openTrialPoster } from "../lib/trialPoster";
import {
  GraduationCap, Plus, Loader2, Trash2, Image as ImageIcon, Save, Palette, Ticket,
  Copy, Link2, Power, QrCode, ChevronDown, ChevronUp, Users, Calendar, TrendingUp, DollarSign, Crown, BarChart3, Settings,
  School, Building2, RefreshCw, UserPlus, Send, ShieldCheck, X,
} from "lucide-react";
import TrialCodeDetail from "./coach/TrialCodeDetail";
import { DurationField, clampDays } from "./coach/DurationField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ---- Staff setup-link result modal ----
function StaffInviteModal({ invite, roleLabel, onClose }) {
  const rawUrl = invite?.invite_url || "";
  const link = /^https?:\/\//i.test(rawUrl) ? rawUrl
    : (invite?.setup_token ? `${window.location.origin}/coach-setup?token=${invite.setup_token}` : rawUrl);
  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(link);
      else { const ta = document.createElement("textarea"); ta.value = link; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove(); }
      toast.success("Copied.");
    } catch { toast.error("Couldn't copy — select the link manually."); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="staff-invite-modal" onClick={onClose}>
      <div className="bg-[#111] border border-[#22C55E]/40 rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3"><Link2 size={16} className="text-[#22C55E]" /><h4 className="font-subheadings text-sm font-bold uppercase tracking-widest text-white/85">Staff Setup Link</h4></div>
        <p className="text-xs text-white/50 mb-4">Send this one-time link to <b className="text-white/80">{invite?.email || "the coach"}</b> ({roleLabel(invite?.role)}). They'll set their own name &amp; password, then get their institution's live dashboard.</p>
        <div className="flex items-center gap-2 mb-3">
          <input data-testid="staff-invite-link-field" readOnly value={link} onFocus={(e) => e.target.select()} className="flex-1 min-w-0 bg-[#0d0d0d] border border-white/10 rounded-md px-3 py-2 text-white/90 text-xs font-mono" />
          <button data-testid="staff-invite-link-copy" onClick={copy} className="p-2 text-white/60 hover:text-white border border-white/10 rounded-md"><Copy size={15} /></button>
        </div>
        <button data-testid="staff-invite-close" onClick={onClose} className="w-full bg-[#C41E24] hover:bg-[#A0161D] text-white text-xs font-bold uppercase tracking-wider py-2.5 rounded-md">Done</button>
      </div>
    </div>
  );
}

function SchoolCard({ token, school, stats, onChange }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [name, setName] = useState(school.name);
  const [primary, setPrimary] = useState(school.primary_color || "#C41E24");
  const [secondary, setSecondary] = useState(school.secondary_color || "#D4AF37");
  const [type, setType] = useState(school.type || "high_school");
  const [primaryCode, setPrimaryCode] = useState(school.primary_code || "");
  const [teams, setTeams] = useState(school.teams || []);
  const [newTeamName, setNewTeamName] = useState("");
  const [teamBusy, setTeamBusy] = useState(false);
  const [staff, setStaff] = useState(school.staff || []);
  const [staffName, setStaffName] = useState("");
  const [staffEmail, setStaffEmail] = useState("");
  const [staffRole, setStaffRole] = useState("head_coach");
  const [staffBusy, setStaffBusy] = useState(false);
  const [staffInvite, setStaffInvite] = useState(null);
  const [mascot, setMascot] = useState(school.mascot_url);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [codes, setCodes] = useState([]);
  const [days, setDays] = useState("14");
  const [codeDetail, setCodeDetail] = useState("");
  const [label, setLabel] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [busy, setBusy] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [dashOpen, setDashOpen] = useState(false);
  const [roster, setRoster] = useState(null);
  const fileRef = useRef(null);

  const loadRoster = useCallback(() => {
    axios.get(`${API}/coach/schools/${school.id}/roster`, auth).then(({ data }) => setRoster(data)).catch(() => {});
  }, [school.id, token]);
  useEffect(() => { if (dashOpen) loadRoster(); }, [dashOpen, loadRoster]);

  const loadCodes = useCallback(() => {
    axios.get(`${API}/coach/schools/${school.id}/trial-codes`, auth).then(({ data }) => setCodes(data)).catch(() => {});
  }, [school.id, token]);
  useEffect(() => { if (open) loadCodes(); }, [open, loadCodes]);

  const saveBranding = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/coach/schools/${school.id}`,
        { name: name.trim(), primary_color: primary, secondary_color: secondary, type }, auth);
      toast.success("Institution details saved.");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save."); }
    finally { setSaving(false); }
  };

  const regenerateCode = async () => {
    if (!window.confirm(`Generate a NEW invite code for ${school.name}? The current code will stop working immediately.`)) return;
    try {
      const { data } = await axios.post(`${API}/coach/schools/${school.id}/regenerate-code`, {}, auth);
      setPrimaryCode(data.code);
      toast.success(`New invite code: ${data.code}`);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't regenerate."); }
  };

  const addTeam = async () => {
    const n = newTeamName.trim();
    if (!n) return;
    setTeamBusy(true);
    try {
      const { data } = await axios.post(`${API}/coach/schools/${school.id}/teams`, { name: n }, auth);
      setTeams((t) => [...t, data]);
      setNewTeamName("");
      toast.success(`Squad "${data.name}" added — code ${data.code}`);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add squad."); }
    finally { setTeamBusy(false); }
  };
  const regenTeamCode = async (tm) => {
    if (!window.confirm(`Generate a NEW code for "${tm.name}"? The current code will stop working immediately.`)) return;
    try {
      const { data } = await axios.post(`${API}/coach/schools/${school.id}/teams/${tm.id}/regenerate-code`, {}, auth);
      setTeams((ts) => ts.map((x) => x.id === tm.id ? { ...x, code: data.code } : x));
      toast.success(`New code: ${data.code}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't regenerate."); }
  };
  const removeTeam = async (tm) => {
    if (!window.confirm(`Delete squad "${tm.name}"? Its code will stop working.`)) return;
    try {
      await axios.delete(`${API}/coach/schools/${school.id}/teams/${tm.id}`, auth);
      setTeams((ts) => ts.filter((x) => x.id !== tm.id));
      toast.success("Squad deleted.");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete."); }
  };

  const inviteStaff = async () => {
    setStaffBusy(true);
    try {
      const { data } = await axios.post(`${API}/coach/coach-invites`, {
        club_name: school.name, school_id: school.id, role: staffRole,
        name: staffName.trim(), email: staffEmail.trim().toLowerCase() || null,
      }, auth);
      setStaff((s) => [...s, data]);
      setStaffInvite(data);
      setStaffName(""); setStaffEmail("");
      toast.success("Staff invite link generated.");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create the invite."); }
    finally { setStaffBusy(false); }
  };
  const removeStaff = async (st) => {
    if (!window.confirm(`Remove ${st.name || st.email || "this coach"}'s access?`)) return;
    try {
      await axios.delete(`${API}/coach/club-coaches/${st.id}`, auth);
      setStaff((s) => s.filter((x) => x.id !== st.id));
      toast.success("Staff removed.");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove."); }
  };
  const roleLabel = (r) => r === "assistant_coach" ? "Assistant Coach" : "Head Coach";

  const uploadMascot = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/coach/schools/${school.id}/mascot`, fd, auth);
      setMascot(data.mascot_url);
      toast.success("Mascot uploaded.");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
  };

  const removeSchool = async () => {
    if (!window.confirm(`Delete ${school.name}? Its readiness codes will stop working.`)) return;
    try { await axios.delete(`${API}/coach/schools/${school.id}`, auth); toast.success("School deleted."); onChange(); }
    catch { toast.error("Couldn't delete."); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const body = { label: label.trim(), max_uses: maxUses ? parseInt(maxUses, 10) : null, days: clampDays(days || 14) };
      const { data } = await axios.post(`${API}/coach/schools/${school.id}/trial-codes`, body, auth);
      setLabel(""); setMaxUses(""); setDays("14");
      toast.success(`${data.days}-day Readiness code created: ${data.code}`);
      loadCodes();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create code."); }
    finally { setBusy(false); }
  };

  const copyCode = (code) => { navigator.clipboard.writeText(code); toast.success(`Code ${code} copied.`); };
  const copyLink = (code) => { navigator.clipboard.writeText(`${window.location.origin}/redeem?code=${encodeURIComponent(code)}`); toast.success("Shareable link copied."); };
  const toggle = async (c) => { try { await axios.post(`${API}/coach/trial-codes/${c.id}/toggle`, {}, auth); loadCodes(); } catch { toast.error("Couldn't update."); } };
  const remove = async (c) => { if (!window.confirm(`Delete code ${c.code}?`)) return; try { await axios.delete(`${API}/coach/trial-codes/${c.id}`, auth); loadCodes(); } catch { toast.error("Couldn't delete."); } };

  return (
    <div data-testid={`school-card-${school.id}`} className="rounded-xl border overflow-hidden"
      style={{ borderColor: `${primary}44`, background: "#0d0d0d", boxShadow: `0 0 24px ${primary}18` }}>
      <div className="p-5" style={{ background: `linear-gradient(120deg, ${primary}1f, transparent 60%)` }}>
        <div className="flex items-start gap-4">
          <button onClick={() => fileRef.current?.click()} data-testid={`school-mascot-btn-${school.id}`}
            className="w-16 h-16 rounded-xl shrink-0 flex items-center justify-center overflow-hidden bg-black/40 border transition-colors hover:border-white/40"
            style={{ borderColor: `${secondary}66` }} title="Upload mascot / logo">
            {mascot ? <img loading="lazy" decoding="async" src={`${BACKEND}${mascot}`} alt="" className="w-full h-full object-contain p-1" />
              : <ImageIcon size={22} className="text-white/40" />}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden"
            data-testid={`school-mascot-input-${school.id}`}
            onChange={(e) => uploadMascot(e.target.files?.[0])} />
          <div className="flex-1 min-w-0">
            <input data-testid={`school-name-${school.id}`} value={name} onChange={(e) => setName(e.target.value)}
              className="w-full bg-transparent font-headings text-xl uppercase tracking-wide text-white focus:outline-none border-b border-transparent focus:border-white/20 pb-1" />
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[11px] text-white/50">
              <span data-testid={`school-type-badge-${school.id}`} className="inline-flex items-center gap-1 font-bold uppercase tracking-wider rounded-full px-2 py-0.5"
                style={{ color: secondary, background: `${secondary}18`, border: `1px solid ${secondary}44` }}>
                {type === "college" ? <Building2 size={11} /> : <School size={11} />}
                {type === "college" ? "College" : "High School"}
              </span>
              <span className="inline-flex items-center gap-1"><Users size={12} /> {school.player_count} players</span>
              <span className="inline-flex items-center gap-1"><Ticket size={12} /> {school.code_count} codes</span>
            </div>
          </div>
          <button data-testid={`school-delete-${school.id}`} onClick={removeSchool} className="text-white/30 hover:text-[#C41E24] p-1"><Trash2 size={16} /></button>
        </div>

        {/* Per-school dashboard stats */}
        {stats && (
          <div className="grid grid-cols-4 gap-2 mt-4" data-testid={`school-stats-${school.id}`}>
            {[
              { k: "active_trials", label: "Active Trials", val: stats.active_trials, c: "#EAB308" },
              { k: "conversions", label: "Conversions", val: stats.conversions, c: "#22C55E" },
              { k: "paying_members", label: "Paying", val: stats.paying_members, c: "#fff" },
              { k: "mrr", label: "Monthly Rev", val: `$${stats.mrr}`, c: secondary },
            ].map((m) => (
              <div key={m.k} className="rounded-lg bg-black/40 border border-white/10 px-2.5 py-2 text-center">
                <div className="font-cover text-xl leading-none" style={{ color: m.c }}>{m.val}</div>
                <div className="text-[9px] uppercase tracking-wider text-white/40 mt-1">{m.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-2 mt-4">
          <button data-testid={`school-view-${school.id}`} onClick={() => setDashOpen((o) => !o)}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-sm transition-colors text-white"
            style={{ background: primary }}>
            <BarChart3 size={14} /> {dashOpen ? "Hide Dashboard" : "View Dashboard"}
          </button>
          <button data-testid={`school-manage-${school.id}`} onClick={() => setManageOpen((o) => !o)}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-sm transition-colors border border-white/15 text-white/70 hover:text-white">
            <Settings size={14} /> Manage
          </button>
        </div>

        {/* Persistent institution invite code — quick share (like Club Manager team code) */}
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-white/10" data-testid={`school-invite-row-${school.id}`}>
          <Ticket size={13} className="shrink-0" style={{ color: secondary }} />
          <span className="text-[10px] font-bold uppercase tracking-widest text-white/40">{type === "college" ? "Team Code" : "Invite Code"}</span>
          <code data-testid={`school-primary-code-${school.id}`} className="font-mono text-sm font-bold rounded px-2 py-0.5 tracking-wider"
            style={{ color: secondary, background: `${secondary}18`, border: `1px solid ${secondary}44` }}>{primaryCode || "—"}</code>
          <span className="text-[10px] font-bold uppercase tracking-wider text-white/45 bg-white/5 rounded-full px-2 py-0.5">14-day trial</span>
          <div className="flex items-center gap-1.5 ml-auto">
            <button data-testid={`school-primary-poster-${school.id}`} onClick={() => openTrialPoster({ code: primaryCode, teamName: school.name, logoUrl: mascot ? `${BACKEND}${mascot}` : "", accent: secondary, primaryColor: primary })} title="Printable QR flyer" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-black rounded px-2 py-1" style={{ background: secondary }}><QrCode size={12} /> Poster</button>
            <button data-testid={`school-primary-copy-${school.id}`} onClick={() => copyCode(primaryCode)} title="Copy code" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white/55 hover:text-white border border-white/15 rounded px-2 py-1"><Copy size={12} /> Code</button>
            <button data-testid={`school-primary-link-${school.id}`} onClick={() => copyLink(primaryCode)} title="Copy shareable redeem link" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white/55 hover:text-white border border-white/15 rounded px-2 py-1"><Link2 size={12} /> Link</button>
            <button data-testid={`school-primary-regen-${school.id}`} onClick={regenerateCode} title="Generate a new code (old one stops working)" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white/45 hover:text-[#C41E24] px-1.5 py-1"><RefreshCw size={12} /></button>
          </div>
        </div>

        {/* Squads / Teams — each with its own invite code */}
        <div className="mt-4 pt-4 border-t border-white/10" data-testid={`school-teams-${school.id}`}>
            <div className="flex items-center gap-2 mb-2.5">
              <Users size={13} style={{ color: secondary }} />
              <span className="text-[11px] font-bold uppercase tracking-widest text-white/60">Squads / Teams</span>
              <span className="text-[10px] text-white/35">— each squad gets its own invite code</span>
            </div>
            <div className="space-y-2 mb-3">
              {teams.length === 0 && <p className="text-xs text-white/40">No squads yet — add one below (e.g. "Men's Varsity", "Women's D1").</p>}
              {teams.map((tm) => (
                <div key={tm.id} data-testid={`school-team-${tm.id}`} className="bg-black/40 border border-white/10 rounded-lg px-3 py-2.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-white/90 mr-1">{tm.name}</span>
                    <code data-testid={`school-team-code-${tm.id}`} className="font-mono text-sm font-bold rounded px-2 py-0.5 tracking-wider"
                      style={{ color: secondary, background: `${secondary}18`, border: `1px solid ${secondary}44` }}>{tm.code}</code>
                    <div className="flex items-center gap-1.5 ml-auto">
                      <button data-testid={`school-team-poster-${tm.id}`} onClick={() => openTrialPoster({ code: tm.code, teamName: `${school.name} · ${tm.name}`, logoUrl: mascot ? `${BACKEND}${mascot}` : "", accent: secondary, primaryColor: primary })} title="Printable QR flyer" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-black rounded px-2 py-1" style={{ background: secondary }}><QrCode size={12} /> Poster</button>
                      <button data-testid={`school-team-copy-${tm.id}`} onClick={() => copyCode(tm.code)} title="Copy code" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white/55 hover:text-white border border-white/15 rounded px-2 py-1"><Copy size={12} /> Code</button>
                      <button data-testid={`school-team-link-${tm.id}`} onClick={() => copyLink(tm.code)} title="Copy shareable redeem link" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white/55 hover:text-white border border-white/15 rounded px-2 py-1"><Link2 size={12} /> Link</button>
                      <button data-testid={`school-team-regen-${tm.id}`} onClick={() => regenTeamCode(tm)} title="Generate a new code" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white/45 hover:text-[#C41E24] px-1.5 py-1"><RefreshCw size={12} /></button>
                      <button data-testid={`school-team-delete-${tm.id}`} onClick={() => removeTeam(tm)} title="Delete squad" className="text-white/30 hover:text-[#C41E24] p-1"><Trash2 size={13} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input data-testid={`school-new-team-${school.id}`} value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTeam()}
                placeholder="New squad (e.g. Men's Varsity)" className="flex-1 max-w-xs bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20" />
              <button data-testid={`school-add-team-${school.id}`} onClick={addTeam} disabled={teamBusy}
                className="inline-flex items-center gap-1 border border-white/15 hover:border-white/40 text-white/80 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-md disabled:opacity-60">
                {teamBusy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add Squad
              </button>
            </div>
          </div>

        {/* Staff — Head Coach / Assistant Coach with unique setup links */}
        <div className="mt-4 pt-4 border-t border-white/10" data-testid={`school-staff-${school.id}`}>
          <div className="flex items-center gap-2 mb-2.5">
            <ShieldCheck size={13} style={{ color: secondary }} />
            <span className="text-[11px] font-bold uppercase tracking-widest text-white/60">Coaching Staff</span>
            <span className="text-[10px] text-white/35">— invite staff with their own setup link</span>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {staff.length === 0 && <span className="text-xs text-white/40">No staff yet — invite a Head or Assistant Coach below.</span>}
            {staff.map((st) => (
              <span key={st.id} data-testid={`school-staff-chip-${st.id}`} className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider bg-white/5 border border-white/10 rounded-full pl-2.5 pr-1.5 py-1">
                {st.role === "assistant_coach" ? <ShieldCheck size={11} className="text-white/45" /> : <Crown size={11} style={{ color: secondary }} />}
                <span className="text-white/85">{st.name || st.email || "Invited"}</span>
                <span className="text-white/35 normal-case">· {roleLabel(st.role)}{!st.activated && " (pending)"}</span>
                <button data-testid={`school-staff-remove-${st.id}`} onClick={() => removeStaff(st)} className="text-white/30 hover:text-[#C41E24]"><X size={12} /></button>
              </span>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <select data-testid={`school-staff-role-${school.id}`} value={staffRole} onChange={(e) => setStaffRole(e.target.value)}
              className="bg-black/40 border border-white/10 rounded-md px-2.5 py-2 text-xs text-white focus-visible:outline-none">
              <option value="head_coach">Head Coach</option>
              <option value="assistant_coach">Assistant Coach</option>
            </select>
            <input data-testid={`school-staff-name-${school.id}`} value={staffName} onChange={(e) => setStaffName(e.target.value)} placeholder="Coach name"
              className="w-32 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus-visible:outline-none" />
            <input data-testid={`school-staff-email-${school.id}`} type="email" value={staffEmail} onChange={(e) => setStaffEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && inviteStaff()} placeholder="coach@email.com"
              className="w-44 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus-visible:outline-none" />
            <button data-testid={`school-invite-staff-${school.id}`} onClick={inviteStaff} disabled={staffBusy}
              className="inline-flex items-center gap-1 text-black text-[10px] font-bold uppercase tracking-wider px-3 py-2 rounded-md disabled:opacity-60" style={{ background: secondary }}>
              {staffBusy ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />} Invite Staff
            </button>
          </div>
        </div>

        {staffInvite && <StaffInviteModal invite={staffInvite} roleLabel={roleLabel} onClose={() => setStaffInvite(null)} />}

        {/* Manage: color pickers */}
        {manageOpen && (
        <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-white/10">
          <div className="flex items-center gap-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-white/45">Type</label>
            <div className="inline-flex items-center gap-1 bg-black/40 border border-white/10 rounded-full p-1">
              {[["high_school", "High School"], ["college", "College"]].map(([k, lbl]) => (
                <button key={k} data-testid={`school-type-${k}-${school.id}`} onClick={() => setType(k)}
                  className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors"
                  style={{ background: type === k ? secondary : "transparent", color: type === k ? "#000" : "rgba(255,255,255,0.5)" }}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Palette size={13} className="text-white/40" />
            <label className="text-[10px] font-bold uppercase tracking-widest text-white/45">Primary</label>
            <input data-testid={`school-primary-${school.id}`} type="color" value={primary} onChange={(e) => setPrimary(e.target.value)}
              className="w-8 h-8 rounded cursor-pointer bg-transparent border border-white/15" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-white/45">Accent</label>
            <input data-testid={`school-secondary-${school.id}`} type="color" value={secondary} onChange={(e) => setSecondary(e.target.value)}
              className="w-8 h-8 rounded cursor-pointer bg-transparent border border-white/15" />
          </div>
          <button data-testid={`school-save-${school.id}`} onClick={saveBranding} disabled={saving}
            className="ml-auto flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white px-4 py-2 rounded-sm transition-colors disabled:opacity-60"
            style={{ background: primary }}>
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
          </button>
        </div>
        )}
      </div>

      {/* Team dashboard (roster) */}
      {dashOpen && <SchoolDashboard data={roster} accent={primary} secondary={secondary} />}

      {/* Readiness codes toggle (Manage only) */}
      {manageOpen && (<>
      <button data-testid={`school-codes-toggle-${school.id}`} onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-3 border-t border-white/10 text-xs font-bold uppercase tracking-wider text-white/70 hover:text-white transition-colors">
        <span className="flex items-center gap-2"><Calendar size={14} style={{ color: secondary }} /> Elite Readiness Codes</span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {open && (
        <div className="px-5 pb-5">
          <div className="grid sm:grid-cols-2 lg:grid-cols-[1fr_auto_auto_auto] gap-3 items-end mb-4">
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="text-[10px] font-bold uppercase tracking-widest text-white/45 block mb-1.5">Label (optional)</label>
              <input data-testid={`school-code-label-${school.id}`} value={label} onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Fall Tryouts, Varsity" className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20" />
            </div>
            <DurationField value={days} onChange={setDays} presets={[14, 30]} testid={`school-code-duration-${school.id}`} dark="bg-black/40" />
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-white/45 block mb-1.5">Max activations</label>
              <input data-testid={`school-code-max-${school.id}`} type="number" min="1" value={maxUses} onChange={(e) => setMaxUses(e.target.value)}
                placeholder="∞" className="w-24 bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-[16px] sm:text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20" />
              <p className="text-[10px] text-white/40 mt-1">Stops after this many players</p>
            </div>
            <button data-testid={`school-code-generate-${school.id}`} onClick={generate} disabled={busy}
              className="flex items-center justify-center gap-2 text-black font-subheadings font-bold uppercase tracking-wider text-sm px-5 py-2.5 rounded-sm transition-colors disabled:opacity-60"
              style={{ background: secondary }}>
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Generate
            </button>
          </div>

          {codes.length === 0 ? (
            <p className="text-center text-white/40 text-sm py-6 border border-dashed border-white/10 rounded-lg">No readiness codes yet. Generate one to invite this school for an Elite trial.</p>
          ) : (
            <div className="space-y-2.5">
              {codes.map((c) => (
                <div key={c.id} data-testid={`school-code-${c.id}`} className={`bg-black/40 border rounded-lg p-3 ${c.active ? "border-white/10" : "border-white/5 opacity-60"}`}>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <code className="font-mono text-sm font-bold px-2 py-1 rounded tracking-wider"
                      style={{ color: secondary, background: `${secondary}18`, border: `1px solid ${secondary}44` }}>{c.code}</code>
                    <span data-testid={`school-code-days-${c.id}`} className="text-[10px] font-bold uppercase tracking-wider text-white/45 bg-white/5 rounded-full px-2 py-0.5">{c.days || 14} days</span>
                    {c.label && <span className="text-xs text-white/55">{c.label}</span>}
                    {!c.active && <span className="text-[10px] font-bold uppercase text-white/40 bg-white/5 border border-white/15 rounded-full px-2 py-0.5">Paused</span>}
                    {c.status === "expired" && <span className="text-[10px] font-bold uppercase text-[#C41E24] bg-[#C41E24]/10 border border-[#C41E24]/40 rounded-full px-2 py-0.5">Expired</span>}
                    {c.expiring_soon && <span data-testid={`school-code-expiring-${c.id}`} className="text-[10px] font-bold uppercase text-[#FACC15] bg-[#FACC15]/10 border border-[#FACC15]/40 rounded-full px-2 py-0.5">Expiring Soon</span>}
                    <div className="flex flex-wrap items-center gap-1.5 ml-auto">
                      <button data-testid={`school-code-details-${c.id}`} onClick={() => setCodeDetail(c.id)} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#D4AF37] border border-[#D4AF37]/50 hover:bg-[#D4AF37]/10 rounded px-2 py-1">Details</button>
                      {codeDetail === c.id && <TrialCodeDetail token={token} kind="code" id={c.id} onClose={() => setCodeDetail("")} onChanged={loadCodes} />}
                      <button data-testid={`school-code-poster-${c.id}`} onClick={() => openTrialPoster({ code: c.code, teamName: school.name, logoUrl: mascot ? `${BACKEND}${mascot}` : "", accent: secondary, primaryColor: primary })} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-black rounded px-2 py-1" style={{ background: secondary }}><QrCode size={12} /> Poster</button>
                      <button data-testid={`school-code-copy-${c.id}`} onClick={() => copyCode(c.code)} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white/55 hover:text-white border border-white/15 rounded px-2 py-1"><Copy size={12} /> Code</button>
                      <button data-testid={`school-code-link-${c.id}`} onClick={() => copyLink(c.code)} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white/55 hover:text-white border border-white/15 rounded px-2 py-1"><Link2 size={12} /> Link</button>
                      <button data-testid={`school-code-toggle-${c.id}`} onClick={() => toggle(c)} className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border rounded px-2 py-1 ${c.active ? "text-white/55 border-white/15 hover:text-white" : "text-[#22C55E] border-[#22C55E]/40"}`}><Power size={12} /> {c.active ? "Off" : "On"}</button>
                      <button data-testid={`school-code-delete-${c.id}`} onClick={() => remove(c)} className="text-white/30 hover:text-[#C41E24] p-1"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[11px] text-white/45">
                    <span><b className="text-white/80">{c.uses}</b> redeemed{c.max_uses ? ` / ${c.max_uses}` : ""}</span>
                    <span><b className="text-white/80">{c.signups ?? 0}</b> signups</span>
                    <span className="text-[#22C55E]"><b>{c.conversions ?? 0}</b> paid · {c.conversion_rate ?? 0}%</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      </>)}
    </div>
  );
}

function SchoolDashboard({ data, accent = "#C41E24", secondary = "#D4AF37" }) {
  const tone = (s) => (s == null ? "#6b7280" : s >= 85 ? "#22C55E" : s >= 60 ? "#EAB308" : s >= 40 ? "#F97316" : "#EF4444");
  if (!data) return <div className="px-5 py-6 border-t border-white/10 text-sm text-white/40">Loading dashboard…</div>;
  const { roster = [], team_readiness, avg_training, checked_in, count, red_flags } = data;
  return (
    <div className="px-5 py-5 border-t border-white/10 bg-black/30" data-testid="school-dashboard">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label: "Team Readiness", val: team_readiness == null ? "—" : `${team_readiness}%`, c: tone(team_readiness) },
          { label: "Avg Plan Done", val: `${avg_training}%`, c: secondary },
          { label: "Checked In", val: `${checked_in}/${count}`, c: "#fff" },
          { label: "Red Flags", val: red_flags, c: red_flags ? "#EF4444" : "#fff" },
        ].map((m) => (
          <div key={m.label} className="rounded-lg bg-[#141414] border border-white/10 px-3 py-2.5 text-center">
            <div className="font-cover text-2xl leading-none" style={{ color: m.c }}>{m.val}</div>
            <div className="text-[9px] uppercase tracking-wider text-white/40 mt-1">{m.label}</div>
          </div>
        ))}
      </div>
      {roster.length === 0 ? (
        <p className="text-center text-white/40 text-sm py-6">No players linked to this school yet. Share a 14-day readiness code to onboard the roster.</p>
      ) : (
        <div className="space-y-2">
          <div className="hidden sm:grid grid-cols-[1.5fr_1fr_1fr_1fr] gap-2 px-3 text-[10px] font-bold uppercase tracking-wider text-white/35">
            <span>Athlete</span><span>Status</span><span>Readiness</span><span>Plan Done</span>
          </div>
          {roster.map((r) => (
            <div key={r.id} data-testid={`roster-row-${r.id}`} className="grid grid-cols-2 sm:grid-cols-[1.5fr_1fr_1fr_1fr] gap-2 items-center bg-[#141414] border border-white/10 rounded-lg px-3 py-2.5">
              <div className="flex items-center gap-2.5 min-w-0">
                {r.avatar_url ? <img loading="lazy" decoding="async" src={`${BACKEND}${r.avatar_url}`} alt="" className="w-8 h-8 rounded-full object-cover" />
                  : <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold text-white/60">{(r.name || "P")[0]}</div>}
                <div className="min-w-0"><div className="text-sm font-bold text-white truncate">{r.name}</div><div className="text-[10px] text-white/40">{r.team || "—"}</div></div>
              </div>
              <div>
                {r.paying ? <span className="text-[10px] font-bold uppercase tracking-wide text-[#22C55E] bg-[#22C55E]/15 border border-[#22C55E]/40 rounded-full px-2 py-0.5">Paying</span>
                  : r.is_trial ? <span className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5" style={{ color: secondary, background: `${secondary}18`, border: `1px solid ${secondary}44` }}>Trial · {r.trial_days_left}d</span>
                  : <span className="text-[10px] font-bold uppercase tracking-wide text-white/40 bg-white/5 border border-white/15 rounded-full px-2 py-0.5">{r.tier}</span>}
              </div>
              <div className="flex items-center gap-1.5">
                {r.checked_in ? <><span className="font-cover text-lg" style={{ color: tone(r.readiness_score) }}>{r.readiness_score}%</span>{r.worst_level && (r.worst_level === "red" || r.worst_level === "orange") && !r.cleared && <span className="w-2 h-2 rounded-full" style={{ background: r.worst_level === "red" ? "#EF4444" : "#F97316" }} />}</>
                  : <span className="text-xs text-white/30">No check-in</span>}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full bg-black/50 overflow-hidden max-w-[80px]"><div className="h-full rounded-full" style={{ width: `${r.training_pct}%`, background: r.training_pct >= 100 ? "#22C55E" : accent }} /></div>
                <span className="text-xs font-bold text-white/70">{r.training_pct}%</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SchoolManager({ token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [schools, setSchools] = useState([]);
  const [overview, setOverview] = useState(null);
  const [name, setName] = useState("");
  const [primary, setPrimary] = useState("#1E3A8A");
  const [secondary, setSecondary] = useState("#FBBF24");
  const [newType, setNewType] = useState("high_school");
  const [busy, setBusy] = useState(false);
  const [showSchools, setShowSchools] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/coach/schools`, auth).then(({ data }) => setSchools(data)).catch(() => {});
    axios.get(`${API}/coach/schools/overview`, auth).then(({ data }) => setOverview(data)).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const statsMap = {};
  (overview?.schools || []).forEach((s) => { statsMap[s.id] = s; });
  const totals = overview?.totals;

  const create = async () => {
    if (!name.trim()) { toast.error("Enter a school name."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/coach/schools`, { name: name.trim(), primary_color: primary, secondary_color: secondary, type: newType }, auth);
      setName("");
      toast.success("Institution added.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create school."); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="school-manager">
      <div className="flex items-center gap-2 mb-2"><GraduationCap size={18} className="text-[#D4AF37]" /><h2 className="font-headings text-2xl uppercase tracking-wide">Institutional Accounts</h2></div>
      <p className="text-sm text-white/45 mb-6 max-w-2xl">Set up high schools &amp; colleges with their own branding (colors + mascot) and generate 14-day <span className="text-white/70">Elite Readiness</span> trial codes. Players who redeem see their school's identity inside the Readiness Hub.</p>

      {/* Institutional overview dashboard */}
      {totals && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7" data-testid="institution-overview">
          {[
            { k: "active_trials", label: "Active Trials", sub: `${overview.trial_days}-day Elite`, val: totals.active_trials, icon: Ticket, c: "#EAB308" },
            { k: "conversions", label: "Conversions", sub: "trial → paid", val: totals.conversions, icon: TrendingUp, c: "#22C55E" },
            { k: "paying_members", label: "Paying Members", sub: "active subs", val: totals.paying_members, icon: Crown, c: "#fff" },
            { k: "mrr", label: "Monthly Revenue", sub: "recurring", val: `$${totals.mrr}`, icon: DollarSign, c: "#D4AF37" },
          ].map((m) => (
            <div key={m.k} data-testid={`overview-${m.k}`} className="bg-[#141414] border border-white/10 rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-white/45">{m.label}</span>
                <m.icon size={15} style={{ color: m.c }} />
              </div>
              <div className="font-cover text-4xl leading-none" style={{ color: m.c }}>{m.val}</div>
              <div className="text-[11px] text-white/40 mt-1.5">{m.sub}</div>
            </div>
          ))}
        </div>
      )}

      {/* Create */}
      <div className="bg-[#141414] border border-white/10 rounded-xl p-5 mb-7">
        <label className="text-[10px] font-bold uppercase tracking-widest text-white/45 block mb-1.5">New institution</label>
        <div className="grid sm:grid-cols-[1fr_auto_auto_auto_auto] gap-3 items-end">
          <input data-testid="school-new-name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()}
            placeholder="e.g. Lincoln High School or Central State College" className="w-full bg-[#0d0d0d] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" />
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-widest text-white/45">Type</span>
            <div className="inline-flex items-center gap-1 bg-[#0d0d0d] border border-white/10 rounded-full p-1">
              {[["high_school", "H.S."], ["college", "College"]].map(([k, lbl]) => (
                <button key={k} data-testid={`school-new-type-${k}`} onClick={() => setNewType(k)}
                  className="px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors"
                  style={{ background: newType === k ? "#D4AF37" : "transparent", color: newType === k ? "#000" : "rgba(255,255,255,0.5)" }}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-widest text-white/45">Primary</span>
            <input data-testid="school-new-primary" type="color" value={primary} onChange={(e) => setPrimary(e.target.value)} className="w-8 h-8 rounded cursor-pointer bg-transparent border border-white/15" />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-widest text-white/45">Accent</span>
            <input data-testid="school-new-secondary" type="color" value={secondary} onChange={(e) => setSecondary(e.target.value)} className="w-8 h-8 rounded cursor-pointer bg-transparent border border-white/15" />
          </div>
          <button data-testid="school-new-create" onClick={create} disabled={busy}
            className="flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e8c65a] text-black font-subheadings font-bold uppercase tracking-wider text-sm px-5 py-2.5 rounded-sm transition-colors disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Add
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mb-3">
        <span className="font-subheadings text-xs font-bold uppercase tracking-widest text-white/60">Institutions</span>
        <button data-testid="toggle-institutions-accordion" onClick={() => setShowSchools((v) => !v)}
          className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3.5 py-2.5 rounded-md border border-white/15 text-white/70 hover:text-white hover:border-white/40 transition-colors">
          <ChevronDown size={15} className={`transition-transform ${showSchools ? "rotate-180" : ""}`} /> {showSchools ? "Hide" : `Show All (${schools.length})`}
        </button>
      </div>

      {showSchools && (schools.length === 0 ? (
        <div className="text-center text-white/40 text-sm py-10 border border-dashed border-white/10 rounded-xl">No institutions yet. Add a school above to get started.</div>
      ) : (
        <div className="space-y-4">
          {schools.map((s) => <SchoolCard key={s.id} token={token} school={s} stats={statsMap[s.id]} onChange={load} />)}
        </div>
      ))}
    </div>
  );
}
